import React from 'react';
import CommandeEvaluationTab from '../../../composants/AvisPrestataire/CommandeEvaluationTab';
import { CommandeMS, StatusId, TabDescriptor } from '../types';
import CommandeMsHistorique from './CommandeMsHistorique';
import CommandeMsConsultation from './CommandeMsConsultation';
import CommandeMsCandidature from './CommandeMsCandidature';
import CommandeMsMessagerie from './CommandeMsMessagerie';
import CommandeMsMarche from './CommandeMsMarche';
import { MessageChannel } from '../hooks/useCommandeMsUnreadCounts';

interface CommandeMsContentProps {
  commande: CommandeMS;
  activeTab: TabDescriptor | null;
  // Rafraichit la commande apres une action (remplace window.location.reload()). Cable des l'etape
  // squelette ; consomme par les onglets a action (Notifications) quand ils arrivent.
  refetch: () => Promise<void> | void;
  // Bascule vers un onglet par son `kind` (ex. Candidature -> Notifications apres signature).
  goToTab?: (kind: TabDescriptor['kind']) => void;
  // Vide le badge de non-lus du canal a l'ouverture de l'onglet (Q&R / Messagerie).
  onMarkRead?: (channel: MessageChannel) => void;
}

// Routeur d'onglet par `kind` (jamais par index). Tous les onglets sont branches :
//   - historique        -> CommandeMsHistorique   (fork SuiviDuMarche)
//   - candidature        -> CommandeMsCandidature  (reponse technique + prix + signature OTP)
//   - questions_reponses -> CommandeMsMessagerie   (canal Q&R)
//   - notifications      -> CommandeMsMarche       (signature bilaterale du marche)
//   - messagerie         -> CommandeMsMessagerie   (canal messagerie privee)
//   - evaluation         -> CommandeEvaluationTab  (avis presta, lecture seule ; deverrouille a Valider=11)
const CommandeMsContent: React.FC<CommandeMsContentProps> = ({
  commande,
  activeTab,
  refetch,
  goToTab,
  onMarkRead,
}) => {
  if (!activeTab) return null;

  switch (activeTab.kind) {
    case 'historique':
      return <CommandeMsHistorique commande={commande} />;
    case 'consultation':
      // Miroir lecture seule du descriptif de consultation defini par l'acheteur.
      // goToTab : le bouton "Déposer mon offre" / "Voir l'offre" bascule sur l'onglet Offre.
      return <CommandeMsConsultation commande={commande} goToTab={goToTab} />;
    case 'candidature':
      // goToTab : le bouton "Accéder au marché signé" bascule sur Notifications. Il ne depend PAS de
      // la preuve de depot (`depotOffre`, absente de certaines donnees anciennes) mais de la seule
      // atteignabilite de l'onglet destination.
      return <CommandeMsCandidature commande={commande} goToTab={goToTab} />;
    case 'questions_reponses':
      // goToTab : le bouton "Déposer mon offre" / "Voir l'offre" bascule sur l'onglet Offre.
      return (
        <CommandeMsMessagerie
          commande={commande}
          channel="questions_reponses"
          onMarkRead={onMarkRead}
          goToTab={goToTab}
        />
      );
    case 'notifications':
      // goToTab : le bouton "Aller dans la messagerie" (marche signe) bascule d'onglet.
      return <CommandeMsMarche commande={commande} refetch={refetch} goToTab={goToTab} />;
    case 'messagerie':
      // goToTab : le bouton "Évaluer l'acheteur" bascule sur l'onglet Evaluation.
      return (
        <CommandeMsMessagerie
          commande={commande}
          channel="messagerie"
          onMarkRead={onMarkRead}
          goToTab={goToTab}
        />
      );
    case 'evaluation':
      // Cote prestataire : lecture seule (l'acheteur depose l'avis). Deverrouille au marche signe (11).
      // Calque la case soeur BC (CommandeContent) et l'acheteur MS (OperationContent), typeCommande="MS".
      return (
        <>
          <CommandeEvaluationTab
            typeCommande="MS"
            commandeId={commande.id}
            validated={(commande.status?.id ?? commande.id_status) === StatusId.Valider}
          />
          <CommandeEvaluationTab
            typeCommande="MS"
            commandeId={commande.id}
            validated={(commande.status?.id ?? commande.id_status) === StatusId.Valider}
            cible="acheteur"
          />
        </>
      );
    default:
      return null;
  }
};

export default CommandeMsContent;
